import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const UnreadMessagesContext = createContext();

export const useUnreadMessages = () => {
  const context = useContext(UnreadMessagesContext);
  if (!context) {
    throw new Error('useUnreadMessages must be used within an UnreadMessagesProvider');
  }
  return context;
};

export const UnreadMessagesProvider = ({ children }) => {
  const { token } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const lastUnreadCountRef = useRef(0);
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const fetchUnreadCount = useCallback(async (showNotification = true) => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newCount = response.data.unread_count || 0;
      
      // Show notification if new messages arrived (only if showNotification is true)
      if (showNotification && newCount > lastUnreadCountRef.current && lastUnreadCountRef.current >= 0) {
        const newMessages = newCount - lastUnreadCountRef.current;
        if (newMessages > 0) {
          toast.info(`📬 ${newMessages} nouveau${newMessages > 1 ? 'x' : ''} message${newMessages > 1 ? 's' : ''}`, {
            duration: 5000,
            action: {
              label: 'Voir',
              onClick: () => navigate('/messages')
            }
          });
          // Play notification sound
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJOur7aBgX93e36LnaijmIx/eoGGioyOhnt1eIKQnJyXjYF4dXuDj5iXkId7c3R6h5KYlIuBd3N2foiRlpKJf3VzdnyGj5WTiX91c3V7hI6Uk4p/dXN1e4SNk5KKf3VzdXuEjZOSin91c3V7hI2TkoqAdXN1e4SNk5KKgHVzdXuEjZOSioB1c3V7hI2Tkoq');
            audio.volume = 0.3;
            audio.play().catch(() => { /* silent */ });
          } catch (e) {
            // Silent fail for audio
          }
        }
      }
      
      lastUnreadCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch (error) {
      // Silent fail for unread count
    }
  }, [token, API, navigate]);

  // Fetch on mount and periodically
  useEffect(() => {
    if (!token) return;
    
    // Initial fetch (without notification on first load)
    const initialFetch = async () => {
      try {
        const response = await axios.get(`${API}/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const count = response.data.unread_count || 0;
        lastUnreadCountRef.current = count;
        setUnreadCount(count);
      } catch (error) {
        // Silent fail
      }
    };
    initialFetch();

    // Periodic fetch with notifications
    const interval = setInterval(() => fetchUnreadCount(true), 30000);
    return () => clearInterval(interval);
  }, [token, API, fetchUnreadCount]);

  // Function to refresh count (called after marking messages as read)
  const refreshUnreadCount = useCallback(() => {
    fetchUnreadCount(false); // Don't show notification when refreshing after read
  }, [fetchUnreadCount]);

  // Function to decrement count manually (optimistic update)
  const decrementUnreadCount = useCallback((amount = 1) => {
    setUnreadCount(prev => Math.max(0, prev - amount));
    lastUnreadCountRef.current = Math.max(0, lastUnreadCountRef.current - amount);
  }, []);

  const value = {
    unreadCount,
    refreshUnreadCount,
    decrementUnreadCount,
    fetchUnreadCount
  };

  return (
    <UnreadMessagesContext.Provider value={value}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};

export default UnreadMessagesContext;
